/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isSameOrigin } from "@cosmos/common-utils";
import { AuthBox, CosmosComponentContextProps } from "@cosmos/components";
import { useAccountId, useAuth } from "@cosmos/next-hooks";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";

import { TemplateProviders } from "../templates";

interface CosmosPageProps {
	componentContext: CosmosComponentContextProps;
	/** Override for NEXT_PUBLIC_EPIC_SERVER_URL (for unified app where env can't vary per-site) */
	serverUrl?: string;
}

export const CosmosPageAuth = ({ componentContext, serverUrl }: CosmosPageProps): React.JSX.Element => {
	useAuth();
	const accountId = useAccountId();
	const searchParams = useSearchParams();
	const router = useRouter();
	const state = searchParams.get("state") || searchParams.get("redirectUrl") || "";

	useEffect(() => {
		if (accountId) {
			const stateValue = Array.isArray(state) ? state[0] : state;
			if (stateValue && isSameOrigin(stateValue)) {
				router.push(stateValue);
			}
		}
	}, [accountId, state, router]);

	// Strip malicious state/redirectUrl param from the URL when it fails origin validation
	useEffect(() => {
		const stateValue = Array.isArray(state) ? state[0] : state;
		if (!stateValue || isSameOrigin(stateValue)) return;
		const cleaned = new URLSearchParams(searchParams.toString());
		cleaned.delete("state");
		cleaned.delete("redirectUrl");
		const qs = cleaned.toString();
		router.replace(`${window.location.pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
	}, [state, searchParams, router]);

	// Direct process.env access required for Next.js NEXT_PUBLIC_* static replacement at build time
	const epicServerUrlStr = serverUrl || process.env.NEXT_PUBLIC_EPIC_SERVER_URL || "https://www.unrealengine.com";
	const finalServerUrl = new URL(epicServerUrlStr);

	return (
		<TemplateProviders componentContext={componentContext}>
			<AuthBox origin={finalServerUrl.origin} />
		</TemplateProviders>
	);
};
